// src/services/homeFeedMaterialized.ts
//
// FAZ D — Materialized HOME feed okuyucu.
//
// Kaynak: users/{uid}/followingFeed (fan-out-on-write; klip + gönderi).
//   - Cloud Functions (myclips_v3_following_feed.ts) yazar; client read-only.
//   - clipTarih DESC tek-alan sıralama → composite index gerekmez.
//   - Her item per-doc getDoc ile hidrate edilir → rules canReadPrivateContent
//     her okumayı tek tek gate'ler (özel hesabın içeriği yalnız takipçiye gelir;
//     unfollow/block sonrası entry zaten cleanup trigger'ıyla silinmiş olur).
//
// Çıktı şekli Feed.tsx'in beklediği HAM doc şekliyle birebir aynıdır
// ({ ...data, id, type:'clip'|'post' }) — rank yolundaki
// fetchReadyClipsForAuthorsPage / posts getDocs ile aynı. Bu yüzden Feed.tsx
// feedItems merge + sortHomeFeed + kart render'ı değişmeden çalışır.
//
// FALLBACK SÖZLEŞMESİ: rawCount === 0 ise feed bu kullanıcı için henüz
// materialize edilmemiştir → Feed.tsx mevcut chunked-rank yoluna düşer.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";
import { db } from "../firebase";
import { getBlockedUids } from "../services/userBlocks";

const PAGE_SIZE_DEFAULT = 12;

/**
 * Tek sayfa materialized home feed çek.
 * @param {{ uid: string, cursor?: any, pageSize?: number }} opts
 * @returns {Promise<{ items: any[], nextCursor: any, hasMore: boolean, rawCount: number }>}
 */
export async function fetchHomeFeedMaterializedPage({
  uid,
  cursor = null,
  pageSize = PAGE_SIZE_DEFAULT,
}: { uid?: string; cursor?: any; pageSize?: number } = {}) {
  if (!uid) {
    return { items: [], nextCursor: null, hasMore: false, rawCount: 0 };
  }

  const fetchLimit = pageSize * 2; // hidrasyon kayıplarına telafi payı

  let q = query(
    collection(db, "users", uid, "followingFeed"),
    orderBy("clipTarih", "desc"),
  );
  q = cursor
    ? query(q, startAfter(cursor), fsLimit(fetchLimit))
    : query(q, fsLimit(fetchLimit));

  const [feedSnap, blockedUids] = await Promise.all([
    getDocs(q),
    getBlockedUids(uid).catch(() => []),
  ]);

  const rawCount = feedSnap.docs.length;
  if (rawCount === 0) {
    return { items: [], nextCursor: null, hasMore: false, rawCount: 0 };
  }

  const blockedSet = new Set((blockedUids || []).map(String));
  // Hidrasyon planı — her feed entry'sini (clip|post) çöz.
  const plan: Array<{ type: "clip" | "post"; itemId: string }> = [];
  for (const d of feedSnap.docs) {
    const data: any = d.data() || {};
    if (data.authorId && blockedSet.has(String(data.authorId))) continue;
    // type yoksa eski (clip-only) şema → "clip".
    const type: "clip" | "post" = data.type === "post" ? "post" : "clip";
    // itemId yoksa: post → "post_" prefix'ini sıyır, clip → bare doc id.
    let itemId = data.itemId || data.clipId || "";
    if (!itemId) {
      itemId =
        type === "post" && d.id.startsWith("post_")
          ? d.id.slice("post_".length)
          : d.id;
    }
    if (!itemId) continue;
    plan.push({ type, itemId: String(itemId) });
  }

  const snaps = await Promise.all(
    plan.map((p) =>
      getDoc(doc(db, p.type === "post" ? "posts" : "clips", p.itemId)).catch(
        () => null,
      ),
    ),
  );

  const seen = new Set<string>();
  const items: any[] = [];
  for (let i = 0; i < snaps.length; i++) {
    const snap = snaps[i];
    if (!snap || !snap.exists()) continue; // silinmiş / rules-denied → ele
    const type = plan[i].type;
    const data: any = snap.data() || {};
    if (data.visible === false) continue; // moderasyon gizlemesi
    if (type === "clip" && data.v3_ready !== true) continue; // hazır değil
    const key = `${type}:${snap.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ ...data, id: snap.id, type });
  }
  // feedSnap clipTarih DESC sıralı; plan/snaps o sırayı korur. Feed.tsx
  // feedItems ayrıca sortHomeFeed/skor uygular.

  const hasMore = rawCount === fetchLimit;
  const nextCursor = hasMore ? feedSnap.docs[rawCount - 1] : null;

  return { items, nextCursor, hasMore, rawCount };
}

export default fetchHomeFeedMaterializedPage;
